import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Root } from './Root.tsx';
import { acquireDemoLock, createGateways } from './gateway/index.ts';
import { startEmbed } from './embed.ts';
import './styles.css';
import './skin.css';

const root = createRoot(document.getElementById('root')!);

/** The boot screen (index.html) stays at least ~1.6 s from page start, then fades out; tests skip the wait. */
function dismissBoot() {
  const el = document.getElementById('boot');
  if (!el) return;
  const fast = navigator.webdriver || matchMedia('(prefers-reduced-motion: reduce)').matches;
  setTimeout(
    () => {
      el.classList.add('out');
      setTimeout(() => el.remove(), 650);
    },
    fast ? 0 : Math.max(0, 1600 - performance.now()),
  );
}

function fatal(message: string) {
  dismissBoot();
  root.render(
    <div className="fatal" role="alert">
      <h1>UrbanRats</h1>
      <p>{message}</p>
    </div>,
  );
}

async function start(steal = false) {
  const lock = await acquireDemoLock(steal);
  if (lock === 'LOCKED') {
    dismissBoot();
    root.render(
      <div className="fatal" role="alert" data-testid="tab-locked">
        <h1>UrbanRats</h1>
        <p>The demo is already open in another tab. Only one tab can run the demo wallet at a time.</p>
        <button type="button" className="confirm" onClick={() => void start(true)}>
          Use it here instead
        </button>
      </div>,
    );
    return;
  }
  try {
    const gw = await createGateways(lock);
    const embed = startEmbed(gw.embedAllowedOrigins, gw.walletCurrency);
    root.render(
      <StrictMode>
        <Root root={gw} embed={embed} />
      </StrictMode>,
    );
    dismissBoot();
  } catch (e) {
    fatal(e instanceof Error ? e.message : String(e));
  }
}

// DEMO-ONLY: live-round intro preview. `#intro-preview` resolves to an empty stand-in outside demo mode
// (the Vite guard refuses the real module there, in dev servers as well as production builds).
if (import.meta.env.MODE === 'demo' && new URLSearchParams(location.search).get('dev') === 'live-intro') {
  void import('#intro-preview').then(({ IntroPreview }) => (dismissBoot(), root.render(<IntroPreview />)));
} else void start();
